/**
 * Reader navigation selector: book → chapter → verse.
 *
 * Self-contained component (part of the `reader` feature). Renders a dialog
 * with three tabs (Book / Chapter / Verse). When a destination is selected it calls
 * `onNavigate({ bookId, abbr, chapter, verse? })`; the container decides the route.
 *
 * URL addressability (#17): the selector is **controlled by the URL**. Each step
 * of the drill-down lives in `?nav=` (`book`, `book:gen`, `book:gen:4`), so the
 * open popup, the book-chapter step, and the chapter-verse step
 * EACH have their own copyable URL that reopens exactly that screen in
 * another browser. The component does not store the step in local signals: it reads
 * it from `props.step` and step advances call `onStepChange` (the container
 * writes it to the URL with `replace`).
 *
 * The verse count for the selected chapter is obtained OFFLINE from the local
 * database (bible_verse_content). If the chapter is not seeded locally, it
 * falls back to a generic grid based on a reasonable maximum so the selector
 * remains usable without the network.
 */

import { createResource, For, Show } from "solid-js";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { Dialog } from "@/components/ui/dialog";
import { bibleBooks, bookChapters } from "@/utils/bible-books-data";
import { readerStore } from "@/store/reader-store";
import { ArrowDownAZ, ListOrdered } from "@fleet/icons";
import type { PowerSyncDrizzleDB } from "@bso/powersync";
import { loadChapterFromLocal } from "../queries/load-chapter";

const LAST_OT_BOOK_ID = 39;
const FALLBACK_VERSE_COUNT = 40;

export interface ReaderNavTarget {
  bookId: number;
  abbr: string;
  chapter: number;
  verse?: number;
}

/** Active selector step, governed by the container URL (`?nav=`). */
export interface ReaderNavStep {
  tab: "book" | "chapter" | "verse";
  bookAbbr?: string;
  chapter?: number;
}

interface ReaderNavSelectorProps {
  open: boolean;
  /** Active step (source of truth in the URL). `undefined` = popup closed. */
  step: ReaderNavStep | undefined;
  /** Moves forward/back one step by writing the new step to the URL. */
  onStepChange: (step: ReaderNavStep) => void;
  /** Closes the popup (clears `?nav=`). */
  onClose: () => void;
  /** Current version (abbr lowercase, e.g. "kjv"). */
  version: string;
  /** Currently open book (used to highlight the active one). */
  currentBookAbbr: string;
  /** Currently open chapter (used to highlight the active one). */
  currentChapter: number;
  /** Drizzle/PowerSync client for the offline verse count. */
  db: PowerSyncDrizzleDB | null;
  onNavigate: (target: ReaderNavTarget) => void;
}

interface SelectedBook {
  id: number;
  name: MessageDescriptor;
  abbr: string;
}

export function ReaderNavSelector(props: ReaderNavSelectorProps) {
  const { t } = useI18n();

  // State derived from the URL (props.step) — NOT from local signals.
  const activeTab = (): "book" | "chapter" | "verse" => props.step?.tab ?? "book";
  const selectedBook = (): SelectedBook | null => {
    const abbr = props.step?.bookAbbr;
    if (!abbr) {
      return null;
    }
    const book = bibleBooks.find((b) => b.abbr === abbr);
    return book ? { id: book.id, name: book.name, abbr: book.abbr } : null;
  };
  const pendingChapter = (): number | null => props.step?.chapter ?? null;

  /**
   * Books in a testament, in the selected order. Alphabetical sorting uses the
   * name **already translated** and `localeCompare`: the published app only had
   * English, but here "Exodus" and "Ezekiel" must sort correctly across the 11
   * locales (and in `ar`/`he`, which also read right-to-left).
   */
  const orderedBooks = (predicate: (book: (typeof bibleBooks)[number]) => boolean) => {
    const subset = bibleBooks.filter((b) => predicate(b));
    if (readerStore.bookOrder !== "alphabetical") {
      return subset;
    }
    return subset.toSorted((a, b) => t(a.name).localeCompare(t(b.name)));
  };

  // Verse count for the pending chapter, from the local database.
  const [verseCount] = createResource(
    () => {
      const b = selectedBook();
      const ch = pendingChapter();
      return b && ch ? { book: b.id, chapter: ch } : null;
    },
    // The createResource fetcher runs inside the scope tracked by its
    // source (above); reading props.db/props.version here is not lost
    // reactivity. False positive for solid/reactivity.
    // oxlint-disable-next-line solid/reactivity
    async (q): Promise<number> => {
      const verses = await loadChapterFromLocal(props.db, props.version, q.book, q.chapter);
      return verses.length > 0 ? verses.length : FALLBACK_VERSE_COUNT;
    },
  );

  function openBook(book: SelectedBook) {
    // Book-chapter step → its own URL (`?nav=book:<abbr>`).
    props.onStepChange({ tab: "chapter", bookAbbr: book.abbr });
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) {
          props.onClose();
        }
      }}
    >
      <div
        class="p-4 md:p-6 max-h-[80vh] overflow-y-auto flex flex-col select-none"
        data-testid="reader-nav"
      >
        <h2 class="text-lg font-bold text-foreground mb-4 shrink-0">
          {t(msg`Biblical Navigation`)}
        </h2>

        {/* Tabs */}
        <div class="flex bg-muted/40 p-1 rounded-xl mb-4 shrink-0">
          <button
            type="button"
            onClick={() => props.onStepChange({ tab: "book" })}
            class="flex-1 py-2 text-xs font-bold rounded-lg text-center transition-all duration-150"
            classList={{
              "bg-card text-foreground shadow-sm": activeTab() === "book",
              "text-muted-foreground hover:text-foreground": activeTab() !== "book",
            }}
          >
            {t(msg`Book`)}
          </button>
          <button
            type="button"
            onClick={() => {
              const b = selectedBook();
              if (b) {
                props.onStepChange({ tab: "chapter", bookAbbr: b.abbr });
              }
            }}
            disabled={!selectedBook()}
            class="flex-1 py-2 text-xs font-bold rounded-lg text-center transition-all duration-150 disabled:opacity-40"
            classList={{
              "bg-card text-foreground shadow-sm": activeTab() === "chapter",
              "text-muted-foreground hover:text-foreground": activeTab() !== "chapter",
            }}
          >
            {t(msg`Chapter`)}
          </button>
          <button
            type="button"
            onClick={() => {
              const b = selectedBook();
              const ch = pendingChapter();
              if (b && ch) {
                props.onStepChange({ tab: "verse", bookAbbr: b.abbr, chapter: ch });
              }
            }}
            disabled={!pendingChapter()}
            class="flex-1 py-2 text-xs font-bold rounded-lg text-center transition-all duration-150 disabled:opacity-40"
            classList={{
              "bg-card text-foreground shadow-sm": activeTab() === "verse",
              "text-muted-foreground hover:text-foreground": activeTab() !== "verse",
            }}
          >
            {t(msg`Verse`)}
          </button>
        </div>

        {/* Books */}
        <Show when={activeTab() === "book"}>
          <div class="space-y-4 flex-1 overflow-y-auto pr-1">
            {/* Toggle canónico ↔ A-Z (paridad con `OrderBooksByButton`): sirve
              para encontrar un libro cuyo lugar en el canon no se recuerda. */}
            <div class="flex justify-end">
              <button
                type="button"
                data-testid="book-order-toggle"
                onClick={() => readerStore.toggleBookOrder()}
                aria-pressed={readerStore.bookOrder === "alphabetical"}
                class="flex items-center gap-1.5 px-2.5 py-1.5 min-h-11 text-2xs font-bold rounded-lg bg-muted/40 hover:bg-muted/70 border border-border/50 text-foreground transition-colors"
                title={
                  readerStore.bookOrder === "alphabetical"
                    ? t(msg`Sort books in canonical order`)
                    : t(msg`Sort books alphabetically`)
                }
              >
                <Show
                  when={readerStore.bookOrder === "alphabetical"}
                  fallback={<ListOrdered class="w-3.5 h-3.5" />}
                >
                  <ArrowDownAZ class="w-3.5 h-3.5" />
                </Show>
                <span>
                  {readerStore.bookOrder === "alphabetical" ? t(msg`A-Z`) : t(msg`Canonical order`)}
                </span>
              </button>
            </div>
            <div>
              <h3 class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 mb-2">
                {t(msg`Old Testament`)}
              </h3>
              <div class="grid grid-cols-3 gap-2">
                <For each={orderedBooks((b) => b.id <= LAST_OT_BOOK_ID)}>
                  {(book) => (
                    <button
                      type="button"
                      onClick={() => openBook(book)}
                      class="px-2 py-2.5 text-xs font-semibold rounded-xl bg-card border border-border/30 hover:border-primary/50 hover:text-primary text-center transition-all truncate"
                      classList={{
                        "bg-primary/10 border-primary text-primary font-bold":
                          props.currentBookAbbr === book.abbr,
                      }}
                    >
                      {t(book.name)}
                    </button>
                  )}
                </For>
              </div>
            </div>

            <div>
              <h3 class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 mb-2">
                {t(msg`New Testament`)}
              </h3>
              <div class="grid grid-cols-3 gap-2">
                <For each={orderedBooks((b) => b.id > LAST_OT_BOOK_ID)}>
                  {(book) => (
                    <button
                      type="button"
                      onClick={() => openBook(book)}
                      class="px-2 py-2.5 text-xs font-semibold rounded-xl bg-card border border-border/30 hover:border-primary/50 hover:text-primary text-center transition-all truncate"
                      classList={{
                        "bg-primary/10 border-primary text-primary font-bold":
                          props.currentBookAbbr === book.abbr,
                      }}
                    >
                      {t(book.name)}
                    </button>
                  )}
                </For>
              </div>
            </div>
          </div>
        </Show>

        {/* Chapters */}
        <Show when={activeTab() === "chapter" && selectedBook()}>
          {(b) => (
            <div class="flex flex-col flex-1 overflow-y-auto">
              <div class="flex items-center gap-2 mb-4 shrink-0">
                <button
                  type="button"
                  onClick={() => props.onStepChange({ tab: "book" })}
                  class="text-xs font-semibold text-primary hover:underline"
                >
                  ← {t(msg`Books`)}
                </button>
                <span class="text-xs text-muted-foreground/60">|</span>
                <span class="text-sm font-bold text-foreground">{t(b().name)}</span>
              </div>
              <div class="grid grid-cols-5 gap-2 pr-1">
                <For each={Array.from({ length: bookChapters[b().id] ?? 1 }, (_, i) => i + 1)}>
                  {(ch) => (
                    <button
                      type="button"
                      onClick={() => {
                        // Chapter-verse step → its own URL.
                        props.onStepChange({ tab: "verse", bookAbbr: b().abbr, chapter: ch });
                      }}
                      class="h-10 flex items-center justify-center text-xs font-bold rounded-xl bg-card border border-border/30 hover:border-primary hover:text-primary transition-all"
                      classList={{
                        "bg-primary/10 border-primary text-primary font-bold":
                          props.currentBookAbbr === b().abbr && props.currentChapter === ch,
                      }}
                    >
                      {ch}
                    </button>
                  )}
                </For>
              </div>
            </div>
          )}
        </Show>

        {/* Verses */}
        <Show when={activeTab() === "verse" && selectedBook() && pendingChapter()}>
          <div class="flex flex-col flex-1 overflow-y-auto">
            <div class="flex items-center gap-2 mb-3 shrink-0">
              <button
                type="button"
                onClick={() => {
                  const b = selectedBook();
                  if (b) {
                    props.onStepChange({ tab: "chapter", bookAbbr: b.abbr });
                  }
                }}
                class="text-xs font-semibold text-primary hover:underline"
              >
                ← {t(msg`Chapters`)}
              </button>
              <span class="text-xs text-muted-foreground/60">|</span>
              <span class="text-sm font-bold text-foreground">
                {(() => {
                  const b = selectedBook();
                  return b ? t(b.name) : "";
                })()}{" "}
                {pendingChapter()}
              </span>
            </div>

            <button
              type="button"
              onClick={() => {
                const b = selectedBook();
                const ch = pendingChapter();
                if (!b || !ch) {
                  return;
                }
                // Navigating to the destination clears `?nav=` (closes the selector); we do
                // not close it separately to avoid overwriting the navigation (Pattern A).
                props.onNavigate({ bookId: b.id, abbr: b.abbr, chapter: ch });
              }}
              class="mb-3 w-full py-2.5 bg-primary text-primary-foreground hover:bg-primary/95 text-xs font-bold rounded-xl transition-all shadow-sm active:scale-98 shrink-0"
            >
              {t(msg`Read full chapter`)}
            </button>

            <div class="grid grid-cols-6 gap-2 pr-1">
              <For
                each={Array.from({ length: verseCount() ?? FALLBACK_VERSE_COUNT }, (_, i) => i + 1)}
              >
                {(v) => (
                  <button
                    type="button"
                    onClick={() => {
                      const b = selectedBook();
                      const ch = pendingChapter();
                      if (!b || !ch) {
                        return;
                      }
                      props.onNavigate({ bookId: b.id, abbr: b.abbr, chapter: ch, verse: v });
                    }}
                    class="h-9 flex items-center justify-center text-xs font-bold rounded-xl bg-card border border-border/30 hover:border-primary hover:text-primary transition-all"
                  >
                    {v}
                  </button>
                )}
              </For>
            </div>
          </div>
        </Show>
      </div>
    </Dialog>
  );
}
